/**
 * @fileoverview Utility functions for determining document upload status.
 *
 * This module provides helper functions to aggregate and determine the overall
 * upload status of documents associated with J-codes in medical PA orders.
 *
 * @module documentUploadStatus
 */

import { JCodeDetails } from "../data-model/medicalPaOrdersModel";
import { DocWriteBackStatus } from "../enums/evBvWriteBackStatus";

/**
 * Determines the aggregate upload status for all documents within a J-code.
 *
 * @description
 * This function evaluates the upload status of all documents associated with
 * a J-code and returns the most critical status based on the following priority:
 *
 * 1. **ERROR** - If any document has an error status, returns ERROR
 * 2. **IN_PROGRESS** - If any document is currently uploading, returns IN_PROGRESS
 * 3. **SUCCESS** - If any document was successfully uploaded, returns SUCCESS
 * 4. **NOT_INITIATED** - If no documents exist or none have been processed
 *
 * @param {JCodeDetails} jCode - The J-code object containing document details
 * @returns {DocWriteBackStatus} The aggregate upload status for the J-code's documents
 *
 * @example
 * // J-code with a failed document upload
 * const jCodeWithError = {
 *   documents: [
 *     { upload_status: DocWriteBackStatus.SUCCESS },
 *     { upload_status: DocWriteBackStatus.ERROR }
 *   ]
 * };
 * getDocumentUploadStatusForJcode(jCodeWithError); // Returns DocWriteBackStatus.ERROR
 *
 * @example
 * // J-code with no documents
 * const jCodeNoDocuments = { documents: [] };
 * getDocumentUploadStatusForJcode(jCodeNoDocuments); // Returns DocWriteBackStatus.NOT_INITIATED
 */
export const getDocumentUploadStatusForJcode = (
  jCode: JCodeDetails,
): DocWriteBackStatus => {
  const documents = jCode?.documents ?? [];
  const uploadStatuses = documents.map((document) => document.upload_status);

  if (uploadStatuses.includes(DocWriteBackStatus.ERROR)) {
    return DocWriteBackStatus.ERROR;
  }

  if (uploadStatuses.includes(DocWriteBackStatus.IN_PROGRESS)) {
    return DocWriteBackStatus.IN_PROGRESS;
  }

  if (uploadStatuses.includes(DocWriteBackStatus.SUCCESS)) {
    return DocWriteBackStatus.SUCCESS;
  }

  return DocWriteBackStatus.NOT_INITIATED;
};
